import type { FieldErrors } from "react-hook-form";
import {
  type BillingAccountDto,
  type CreateBillingAccountDto,
  PaymentTerm,
  type UpdateBillingAccountDto,
} from "client/api-v2";

export interface BillingAccountFormValues {
  legalName: string;
  commonName: string;
  crmCustomerId: string;
  zuoraAccountNumber: string;
  attentionFirstName: string;
  attentionLastName: string;
  attentionEmail: string;
  countryCode: string;
  state: string;
  city: string;
  address1: string;
  address2: string;
  postalCode: string;
  taxId: string;
  paymentTerm: PaymentTerm;
  isTaxExempt: boolean;
  taxExemptionCertificateId: string;
  taxExemptionCertificateType: string;
  taxExemptionJurisdiction: string;
  taxExemptionEntityUseCode: string;
  taxExemptionDescription: string;
  isReseller: boolean;
}

export const defaultBillingAccountFormValues: BillingAccountFormValues = {
  legalName: "",
  commonName: "",
  crmCustomerId: "",
  zuoraAccountNumber: "",
  attentionFirstName: "",
  attentionLastName: "",
  attentionEmail: "",
  countryCode: "",
  state: "",
  city: "",
  address1: "",
  address2: "",
  postalCode: "",
  taxId: "",
  paymentTerm: PaymentTerm.Net30,
  isTaxExempt: false,
  taxExemptionCertificateId: "",
  taxExemptionCertificateType: "",
  taxExemptionJurisdiction: "",
  taxExemptionEntityUseCode: "",
  taxExemptionDescription: "",
  isReseller: false,
};

// The account's own values, not defaults, so opening the dialog in edit mode starts from what's
// actually stored rather than blanks the user would mistake for real edits if they saved without
// touching a field.
export function toBillingAccountFormValues(account: BillingAccountDto): BillingAccountFormValues {
  return {
    legalName: account.legalName,
    commonName: account.commonName ?? "",
    crmCustomerId: account.crmCustomerId ?? "",
    zuoraAccountNumber: account.zuoraAccountNumber ?? "",
    attentionFirstName: account.attentionFirstName,
    attentionLastName: account.attentionLastName,
    attentionEmail: account.attentionEmail,
    countryCode: account.address?.countryCode ?? "",
    state: account.address?.state ?? "",
    city: account.address?.city ?? "",
    address1: account.address?.address1 ?? "",
    address2: account.address?.address2 ?? "",
    postalCode: account.address?.postalCode ?? "",
    taxId: account.taxId ?? "",
    paymentTerm: account.paymentTerm,
    isTaxExempt: account.isTaxExempt,
    taxExemptionCertificateId: account.taxExemptionCertificateId ?? "",
    taxExemptionCertificateType: account.taxExemptionCertificateType ?? "",
    taxExemptionJurisdiction: account.taxExemptionJurisdiction ?? "",
    taxExemptionEntityUseCode: account.taxExemptionEntityUseCode ?? "",
    taxExemptionDescription: account.taxExemptionDescription ?? "",
    isReseller: account.isReseller,
  };
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

type TaxExemptionFields = Pick<
  CreateBillingAccountDto,
  | "taxExemptionCertificateId"
  | "taxExemptionCertificateType"
  | "taxExemptionJurisdiction"
  | "taxExemptionEntityUseCode"
  | "taxExemptionDescription"
>;

function taxExemptionFields(values: BillingAccountFormValues): TaxExemptionFields {
  if (!values.isTaxExempt) {
    return {
      taxExemptionCertificateId: null,
      taxExemptionCertificateType: null,
      taxExemptionJurisdiction: null,
      taxExemptionEntityUseCode: null,
      taxExemptionDescription: null,
    };
  }

  return {
    taxExemptionCertificateId: blankToNull(values.taxExemptionCertificateId),
    taxExemptionCertificateType: blankToNull(values.taxExemptionCertificateType),
    taxExemptionJurisdiction: blankToNull(values.taxExemptionJurisdiction),
    taxExemptionEntityUseCode: blankToNull(values.taxExemptionEntityUseCode),
    taxExemptionDescription: blankToNull(values.taxExemptionDescription),
  };
}

function commonBillingAccountFields(values: BillingAccountFormValues) {
  const address = {
    address1: blankToNull(values.address1),
    address2: blankToNull(values.address2),
    city: blankToNull(values.city),
    state: blankToNull(values.state),
    postalCode: blankToNull(values.postalCode),
    countryCode: blankToNull(values.countryCode),
  };
  // Every address field but address2 is required by the form, so in practice this is always populated
  const hasAddress = Object.values(address).some((value) => value !== null);

  return {
    legalName: values.legalName.trim(),
    commonName: blankToNull(values.commonName),
    crmCustomerId: blankToNull(values.crmCustomerId),
    zuoraAccountNumber: blankToNull(values.zuoraAccountNumber),
    attentionFirstName: values.attentionFirstName.trim(),
    attentionLastName: values.attentionLastName.trim(),
    attentionEmail: values.attentionEmail.trim(),
    address: hasAddress ? address : undefined,
    taxId: blankToNull(values.taxId),
    paymentTerm: values.paymentTerm,
    // Cleared here rather than by letting the unmounted inputs unregister, so a mistaken untick
    // doesn't discard what was typed.
    ...taxExemptionFields(values),
  };
}

export function toCreateBillingAccountDto(values: BillingAccountFormValues): CreateBillingAccountDto {
  // Is Reseller is create-only — the update DTO has no field for it, and the edit dialog disables
  // the checkbox rather than letting it drift out of sync with what the server actually applied.
  return { ...commonBillingAccountFields(values), isReseller: values.isReseller };
}

export function toUpdateBillingAccountDto(values: BillingAccountFormValues): UpdateBillingAccountDto {
  return commonBillingAccountFields(values);
}

/**
 * The form has no nested arrays, so a flat pass over each field's message is enough — no need for
 * createRateCardForm's recursive walk over react-hook-form's nested FieldErrors.
 * @param errors
 */
export function collectErrorMessages(errors: FieldErrors<BillingAccountFormValues>): string[] {
  const messages = Object.values(errors)
    .map((error) => error?.message)
    .filter((message): message is string => Boolean(message));
  return [...new Set(messages)];
}
